import type { GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  type GridFilterModel,
  gridFilterModelSelector,
  type GridPaginationModel,
  gridPaginationModelSelector,
  type GridRowId,
  type GridSortModel,
  gridSortModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import {
  forwardRef,
  type HTMLProps,
  type MouseEvent,
  useRef,
  useState,
} from "react";
import { logger } from "src/logger";
import { css, ThemeProvider } from "@emotion/react";
import { Flex } from "src/components/Flex.tsx";
import { MiniIconButton } from "src/components/MiniIconButton/MiniIconButton.tsx";
import { H5 } from "src/components/HeadingV2/HeadingV2.tsx";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import { CircularProgress } from "@mui/material";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { TooltipV3 } from "src/components/TooltipV3/TooltipV3.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import type { B2bGateSupplierOrderResource } from "@exonn/js-sdk";
import { mapResourceToRow } from "src/admin/lists/b2b-gate-order/mapResourceToRow.ts";
import type { B2bGateOrderRow } from "src/admin/lists/b2b-gate-order/list.types.ts";
import { useErpTranslation } from "src/system/useErpTranslation.ts";

type Row = B2bGateOrderRow;

export const B2bGateOrderDataGroupingColumn = (
  props: GridRenderCellParams<Row, any, any> & {
    onFetchRows: (params: {
      row: Row;
      groupKeys: string[];
      filterModel: GridFilterModel;
      sortModel: GridSortModel;
      paginationModel: GridPaginationModel;
    }) => Promise<{
      rows: B2bGateSupplierOrderResource[];
    }>;
    loadingMap?: Record<GridRowId, boolean>;
    errorMap?: Record<GridRowId, Error>;
  },
) => {
  const { row, rowNode, field, id, onFetchRows, loadingMap, errorMap } = props;

  const { t } = useErpTranslation({
    resource: ResourceEnum.B2bGateOrder,
    type: "list",
  });
  const apiRef = useGridApiContext();
  const theme = useNonConflictingTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isDataLoading = isLoading || loadingMap?.[row.id];
  const [localError, setError] = useState<Error | null>(null);
  const error = localError || errorMap?.[row.id];

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const sortModel = useGridSelector(apiRef, gridSortModelSelector);
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const isFetching = useRef(false);

  const handleClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    // if a row has descendants, and it is not a group yet, it means we didn't load sub rows yet
    if (row.descendants_count > 0 && rowNode.type !== "group") {
      try {
        // if there is ongoing fetch then do nothing
        // helps to avoid double fetch on accidental double click
        if (isFetching.current) {
          return;
        }
        setIsLoading(true);
        setError(null);
        isFetching.current = true;
        const response = await onFetchRows({
          groupKeys: [row.id],
          filterModel,
          paginationModel,
          sortModel,
          row,
        });
        isFetching.current = false;
        setIsLoading(false);

        // put sub-rows in the datagrid rows
        apiRef.current.setRows([
          ...apiRef.current.getSortedRows(),
          ...response.rows.map(mapResourceToRow),
        ]);

        if (response.rows.length > 0) {
          apiRef.current.setRowChildrenExpansion(id, true);
        }
      } catch (error: any) {
        setError(error);
        setIsLoading(false);
        logger.error(error);
      }
    }
    if (rowNode.type === "group") {
      apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    }
    apiRef.current.setCellFocus(id, field);
  };

  const handleDoubleClick = (ev: MouseEvent<HTMLDivElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    // prevents user from redirecting to edit form on double click
  };

  const isExpanded = rowNode.type === "group" && !!rowNode.childrenExpanded;

  if (row.is_child) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <button css={rootStyles()}>
        {isDataLoading && <LoadingIndicator />}
        {error && (
          <TooltipV3 content={t("Failed to load sub orders")}>
            <ErrorIndicator />
          </TooltipV3>
        )}

        <Flex
          flexShrink={0}
          alignItems={"center"}
          justifyContent={"center"}
          css={triggerCell}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
        >
          <MiniIconButton disabled={!row.descendants_count}>
            {isExpanded ? (
              <span className={"icon-15-arw_up"} />
            ) : (
              <span className={"icon-15-arw_dw"} />
            )}
          </MiniIconButton>
        </Flex>
        <Flex flexShrink={1} gap={15} alignItems="center">
          <H5 css={titleStyles}> {row.order_number}</H5>
        </Flex>
      </button>
    </ThemeProvider>
  );
};

const rootStyles = () => css`
  height: 100%;
  width: 100%;
  display: flex;
  border: none;
  position: relative;
  align-items: center;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 10px 0 0;
`;

const titleStyles = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const triggerCell = css`
  width: 40px;
  height: 100%;
`;

export const LoadingIndicator = () => {
  return (
    <div css={loadingIndicatorStyles}>
      <CircularProgress size={10} />
    </div>
  );
};

const loadingIndicatorStyles = css({
  position: "absolute",
  top: 2,
  right: 2,
  display: "flex",
  height: "10px",
});

export const ErrorIndicator = forwardRef<HTMLDivElement>(
  (props: HTMLProps<HTMLDivElement>, ref) => {
    return (
      <div {...props} ref={ref} css={errorIndicatorStyles}>
        <i className={"icon-15-warning"} />
      </div>
    );
  },
);

const errorIndicatorStyles = (theme: ExactTheme) =>
  css({
    position: "absolute",
    top: 2,
    color: theme.colorsV2.red[500],
    right: 2,
    display: "flex",
    height: "10px",
  });
